import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import {
  Modal,
  Button,
  Icon,
  Label,
  Message,
} from 'semantic-ui-react';

import { authService } from '../../utils/auth-service';

const POLL_INTERVAL_MS = 10000;

// "Today" / "Yesterday" / "May 6" / "May 6, 2025" — same logic Slack/Linear use.
const dayLabelFor = unixSeconds => {
  const m = moment.unix(unixSeconds);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  const sendDay = m.clone().startOf('day');
  if (sendDay.isSame(today)) return 'Today';
  if (sendDay.isSame(yesterday)) return 'Yesterday';
  if (m.year() === moment().year()) return m.format('MMM D');
  return m.format('MMM D, YYYY');
};

// Backend returns sends newest-first; preserve that ordering within groups.
const groupSendsByDay = sends => {
  const groups = [];
  sends.forEach(s => {
    const label = dayLabelFor(s.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.sends.push(s);
    } else {
      groups.push({ label, sends: [s] });
    }
  });
  return groups;
};

const COUNT_CHIPS = [
  { key: 'sent', label: 'sent', color: '#1e6f3a', background: '#d4ecdd' },
  { key: 'failed', label: 'failed', color: '#9a2e1f', background: '#fbd9d4' },
  { key: 'skipped', label: 'skipped', color: '#555555', background: '#e6e6e6' },
  { key: 'pending', label: 'pending', color: '#1455a0', background: '#d6e6f6' },
];

const STATUS_ACCENT = {
  COMPLETE: '#2c8a4a',
  PARTIAL: '#e8a317',
  FAILED: '#b94232',
  PROCESSING: '#1f6dbf',
};

const Chip = ({ count, label, color, background }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      color,
      background,
      marginRight: 6,
    }}
  >
    {count} {label}
  </span>
);

Chip.propTypes = {
  count: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
};

const ROW_BASE_STYLE = {
  border: '1px solid #eee',
  borderRadius: 6,
  padding: '14px 18px',
  marginBottom: 10,
  background: '#fff',
  borderLeftWidth: 4,
};

const DayHeading = ({ label }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: '#888',
      margin: '16px 4px 6px',
    }}
  >
    {label}
  </div>
);

DayHeading.propTypes = { label: PropTypes.string.isRequired };

class AdHocEmailHistoryModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sends: null,
      loading: true,
      refreshing: false,
      error: null,
    };
    this._mounted = false;
    this._pollTimer = null;
  }

  componentDidMount() {
    this._mounted = true;
    this.fetch({ isRefresh: false });
  }

  componentWillUnmount() {
    this._mounted = false;
    this.clearPoll();
  }

  clearPoll = () => {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  fetch = async ({ isRefresh = false } = {}) => {
    if (this._mounted) {
      this.setState(isRefresh ? { refreshing: true } : { loading: true });
    }

    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    const { campaignId } = this.props;

    if (!url) {
      this.setState({
        loading: false,
        refreshing: false,
        error: 'REACT_APP_AD_HOC_EMAIL_URL is not configured.',
      });
      return;
    }

    try {
      const token = authService.getToken();
      const response = await axios.get(`${url}/campaigns/${campaignId}/sends`, {
        timeout: 15000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Test sends are excluded from this view — they're noise when the user
      // is reviewing what real recipients received.
      const sends = _.get(response, 'data.data.sends', []).filter(s => !s.isTest);
      if (!this._mounted) return;
      this.setState({ sends, loading: false, refreshing: false, error: null });

      // Auto-poll while anything is still in flight.
      const stillProcessing = sends.some(s => s.status === 'PROCESSING');
      if (stillProcessing) {
        if (!this._pollTimer) {
          this._pollTimer = setInterval(() => this.fetch({ isRefresh: true }), POLL_INTERVAL_MS);
        }
      } else {
        this.clearPoll();
      }
    } catch (err) {
      if (!this._mounted) return;
      const message = _.get(err, 'response.data.error') || err.message || 'Could not load history.';
      this.setState({ loading: false, refreshing: false, error: message });
    }
  }

  renderRow = send => {
    const {
      sendId, createdAt, senderName, senderEmail, subject, counts, status,
      fromName, fromEmail, replyTo, mandrillTemplate,
    } = send;
    const time = createdAt ? moment.unix(createdAt).format('h:mm A') : '—';
    const sender = senderName || senderEmail || 'Unknown';
    const fromLabel = fromName && fromEmail
      ? `${fromName} (${fromEmail})`
      : (fromName || fromEmail || null);
    const isProcessing = status === 'PROCESSING';
    const accent = STATUS_ACCENT[status] || '#ddd';

    return (
      <div
        key={sendId}
        style={{ ...ROW_BASE_STYLE, borderLeftColor: accent }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
            <div
              style={{
                fontSize: 16,
                color: '#1a1a1a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ color: '#888', fontWeight: 'normal', marginRight: 6 }}>
                Subject:
              </span>
              <span style={{ fontWeight: 600 }}>
                {subject || <span style={{ color: '#aaa', fontWeight: 'normal' }}>(no subject)</span>}
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#777', marginTop: 4 }}>
              {time} · sent by {sender}
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 6, lineHeight: 1.6 }}>
              {fromLabel && <div>From: {fromLabel}</div>}
              {replyTo && <div>Reply-to: {replyTo}</div>}
              {mandrillTemplate && <div>Template: {mandrillTemplate}</div>}
            </div>
            <div style={{ marginTop: 10 }}>
              {COUNT_CHIPS.map(c => (
                counts[c.key] > 0 && (
                  <Chip
                    key={c.key}
                    count={counts[c.key]}
                    label={c.label}
                    color={c.color}
                    background={c.background}
                  />
                )
              ))}
            </div>
          </div>
          {isProcessing && (
            <Label color="blue" size="small">
              <Icon loading name="spinner" />
              PROCESSING
            </Label>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { campaignName, onClose } = this.props;
    const { sends, loading, refreshing, error } = this.state;
    const totalCopy = sends ? `${sends.length} send${sends.length === 1 ? '' : 's'}` : ' ';

    return (
      <Modal open size="large" onClose={onClose} closeOnEscape closeOnDimmerClick>
        <Modal.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div>Email sending history</div>
              <div className="small light" style={{ fontWeight: 'normal', fontSize: 13, marginTop: 2 }}>
                Campaign · {campaignName || '—'}
              </div>
            </div>
            <Icon name="close" link onClick={onClose} style={{ marginTop: 4 }} />
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <span className="small light">{totalCopy}</span>
            <Button
              size="tiny"
              basic
              icon
              labelPosition="left"
              loading={refreshing}
              disabled={loading || refreshing}
              onClick={() => this.fetch({ isRefresh: true })}
            >
              <Icon name="refresh" />
              Refresh
            </Button>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#888' }}>
              <Icon name="mail outline" size="huge" />
              <div style={{ marginTop: 12, fontSize: 14 }}>
                Loading sending history…
              </div>
            </div>
          )}

          {!loading && error && (
            <Message negative>
              <Icon name="exclamation circle" />
              {error}
            </Message>
          )}

          {!loading && !error && sends && sends.length === 0 && (
            <Message info>
              <Icon name="info circle" />
              No emails have been sent for this campaign yet.
            </Message>
          )}

          {!loading && !error && sends && sends.length > 0 && (
            <div>
              {groupSendsByDay(sends).map(group => (
                <div key={group.label}>
                  <DayHeading label={group.label} />
                  {group.sends.map(this.renderRow)}
                </div>
              ))}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Close</Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

AdHocEmailHistoryModal.propTypes = {
  campaignId: PropTypes.string.isRequired,
  campaignName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

AdHocEmailHistoryModal.defaultProps = {
  campaignName: '',
};

export default AdHocEmailHistoryModal;



/** WEBPACK FOOTER **
 ** ./src/components/ad-hoc-email-history-modal/index.js
 **/